// node_modules
import { FC, useCallback, useEffect, useState } from "react";
// Components
import { DeleteButton, FindestEditableSpan } from "Components";
// Types
import { TActionObjectDTO, TEnvironmentVariableDTO } from "Types";
// Custom Hooks
import { useDebounce } from "Hooks";
// Enums
import { ToastTypeEnum } from "Enums";
// Helpers
import { ToastHelperSingleton } from "Helpers";
// Controllers
import { IntakeFunctionControllerSingleton } from "Controllers";

export type TCaseIntakeFunctionProps = {
    caseId: number,
    actionObject: TActionObjectDTO,
    keyword?: TEnvironmentVariableDTO,
    intakeFunctionDeletedHandler: (intakeFunctionDeletedId: number) => void
};

export const CaseIntakeFunction: FC<TCaseIntakeFunctionProps> = ({caseId, actionObject, keyword, intakeFunctionDeletedHandler}: TCaseIntakeFunctionProps) => {
    // State
    const [currentActionObject, setCurrentActionObject] = useState<TActionObjectDTO | undefined>(undefined);
    const [currentKeyword, setCurrentKeyword] = useState<TEnvironmentVariableDTO | undefined>(undefined);
    const [actionValue, setActionValue] = useState<string | undefined>(undefined);
    const [objectValue, setObjectValue] = useState<string | undefined>(undefined);
    const [keywordValue, setKeywordValue] = useState<string | undefined>(undefined);

    // Const
    const debouncedActionValue = useDebounce(actionValue, 500);
    const debouncedObjectValue = useDebounce(objectValue, 500);
    const debouncedKeywordValue = useDebounce(keywordValue, 500);

    // Logic
    const updateIntakeFunctionAsync = useCallback(async(newActionValue: string, newObjectValue: string, newKeywordValue: string): Promise<void> => {
        // safety-checks
        if (newActionValue === null || newActionValue === undefined ||
            newObjectValue === null || newObjectValue === undefined ||
            newKeywordValue === null || newKeywordValue === undefined) { return; }

        // call server to update intake function
        const isSuccess: boolean = await IntakeFunctionControllerSingleton
            .updateIntakeFunctionAsync(caseId, actionObject.id, newActionValue, newObjectValue, newKeywordValue);

        // tell user about update result 
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to update intake function.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully updated intake function.");
        }
        
        return;
    }, [actionObject.id, caseId]);

    const deleteIntakeFunctionAsync = async(): Promise<void> => {
        // call server to delete intake function
        const isSuccess: boolean = await IntakeFunctionControllerSingleton
            .deleteIntakeFunctionAsync(actionObject.id);

        // tell user about delete result 
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to delete intake function.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully deleted intake function.");
        }

        // call parent deletion handler
        intakeFunctionDeletedHandler(actionObject.id);
    };

    useEffect(() => {
        setActionValue(undefined);
        setObjectValue(undefined);
        setKeywordValue(undefined);
        setCurrentKeyword(keyword);
        setCurrentActionObject(actionObject);
    }, [actionObject, keyword]);

    useEffect(() => {
        // do not trigger edit at first render, when switching to a new intake function
        // or when displaying a new intake function
        if (!currentActionObject || !currentKeyword || debouncedActionValue === null || debouncedActionValue === undefined) { return; }
        
        setActionValue(undefined);
        currentActionObject.action = debouncedActionValue;
        setCurrentActionObject(currentActionObject);

        // send intake function update to the server
        updateIntakeFunctionAsync(debouncedActionValue, currentActionObject.dobject, currentKeyword.environment);
    }, [currentActionObject, currentKeyword, debouncedActionValue, updateIntakeFunctionAsync]);

    useEffect(() => {
        // do not trigger edit at first render, when switching to a new intake function
        // or when displaying a new intake function
        if (!currentActionObject || !currentKeyword || debouncedObjectValue === null || debouncedObjectValue === undefined) { return; }
        
        setObjectValue(undefined);
        currentActionObject.dobject = debouncedObjectValue;
        setCurrentActionObject(currentActionObject);

        // send intake function update to the server
        updateIntakeFunctionAsync(currentActionObject.action, debouncedObjectValue, currentKeyword.environment);
    }, [currentActionObject, currentKeyword, debouncedObjectValue, updateIntakeFunctionAsync]);

    useEffect(() => {
        // do not trigger edit at first render, when switching to a new intake function
        // or when displaying a new intake function
        if (!currentActionObject || !currentKeyword || debouncedKeywordValue === null || debouncedKeywordValue === undefined) { return; }
        
        setKeywordValue(undefined);
        currentKeyword.environment = debouncedKeywordValue;
        setCurrentKeyword(currentKeyword);

        // send intake function update to the server
        updateIntakeFunctionAsync(currentActionObject.action, currentActionObject.dobject, debouncedKeywordValue);
    }, [currentActionObject, currentKeyword, debouncedKeywordValue, updateIntakeFunctionAsync]);
    
    // Render
    return (
        <>
            {currentActionObject && currentKeyword ?
                    <div>
                        <h6>How might we</h6>
                        <FindestEditableSpan
                            entityId={currentActionObject.id}
                            text={currentActionObject.action}
                            onChangeText={(text: string) => setActionValue(text)}
                            />
                        <FindestEditableSpan
                            entityId={currentActionObject.id}
                            text={currentActionObject.dobject}
                            onChangeText={(text: string) => setObjectValue(text)}
                            />
                        <h6>in/of</h6>
                        <FindestEditableSpan
                            entityId={currentKeyword.id}
                            text={currentKeyword.environment}
                            onChangeText={(text: string) => setKeywordValue(text)}
                            />
                        <DeleteButton
                            onClick={deleteIntakeFunctionAsync}/>
                    </div>
                :
                    null
            }
        </>
    );
};
